import React from 'react';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'components/ThemedECharts';

import Card from 'components/DesignSystem/Card';
import GraficoTelaCheia from 'components/Relatorios/shared/Mobile/GraficoTelaCheia';
import { formatNumberBR } from 'components/Relatorios/shared/RelatorioTable';

import { comFormatadores, criarValueFormatter } from 'helper/Relatorios/Dessem/chartOption';
import useIsMobile from 'utils/hooks/useIsMobile';

import { DessemSubsistemaSerie } from 'types/Relatorios/Dessem';

import './index.scss';

interface Props {
  subsistema: DessemSubsistemaSerie;
  periodos: string[];
  /** Cor do mapa do FE: vale só quando o backend não manda `subsistema.cor`. */
  cor: string;
}

const formatDecimalBR = (n: number): string => n.toLocaleString('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Tooltip em pt-BR com 2 casas fixas e eixo Y em pt-BR, com o `option` do BE e no gráfico montado
 * aqui. Sem o `formatter`, o ECharts escreve o eixo em inglês (PLD acima de 1.000: `1,000`).
 */
const FORMATADORES = { tooltip: criarValueFormatter(formatDecimalBR), eixoY: formatNumberBR };

export default function SubsistemaChart({ subsistema, periodos, cor: corDoMapa }: Props) {
  const isMobile = useIsMobile();
  const cor = subsistema.cor ?? corDoMapa;
  const media = `MÉDIA: ${formatDecimalBR(subsistema.media)}`;

  // No mobile (fora da tela cheia) a média sobe para o topo, ao lado do "MAIOR": o rótulo no fim
  // da linha come ~96px de largura do gráfico, que no celular já é pouca.
  const buildOption = (rotuloNaLinha: boolean): EChartsOption => ({
    xAxis: { type: 'category', data: periodos },
    yAxis: { type: 'value' },
    series: [{
      name: subsistema.sigla,
      type: 'line',
      data: subsistema.valores,
      color: cor,
      symbol: 'circle',
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: { type: 'dashed', color: cor },
        label: {
          show: rotuloNaLinha,
          formatter: media,
          // À direita da linha (Planejamento, 13/10/2026): à esquerda o rótulo cobria o eixo Y.
          position: 'end',
          color: '#434343', // tema: cor de dado (o ThemedECharts adapta)
          backgroundColor: '#fff', // tema: cor de dado (o ThemedECharts adapta)
          padding: [2, 4],
        },
        data: [{ yAxis: subsistema.media }],
      },
    }],
    tooltip: { trigger: 'axis' },
    // `right` em px: espaço para o rótulo "MÉDIA: 62,25" depois do fim da linha (o `containLabel`
    // não conta rótulos de markLine).
    grid: {
      left: '5%', right: rotuloNaLinha ? 96 : 16, top: '15%', bottom: '10%', containLabel: true,
    },
  });

  // O `option` do BE (contrato §2.14) é o layout com a média no fim da linha: vale no desktop e na
  // tela cheia, como veio. No celular fora da tela cheia a média está no topo e o gráfico precisa
  // do layout compacto, que o BE não manda; ali vale a montagem local, sem alterar o `option` do
  // BE.
  const optionDoGrafico = (rotuloNaLinha: boolean) => comFormatadores(
    subsistema.option && rotuloNaLinha ? subsistema.option : buildOption(rotuloNaLinha),
    FORMATADORES,
  );

  return (
    <div className="dessem-subsistema-chart">
      <div className="dessem-subsistema-chart__tab" style={{ backgroundColor: cor }}>
        <span className="dessem-subsistema-chart__sigla">{subsistema.sigla}</span>
        <span className="dessem-subsistema-chart__nome">{subsistema.nome}</span>
      </div>
      <Card className="dessem-subsistema-chart__body">
        <span className="dessem-subsistema-chart__maior">
          {isMobile && <span className="dessem-subsistema-chart__media">{media}</span>}
          {`MAIOR: ${formatDecimalBR(subsistema.maior)}`}
        </span>
        {/* Botão à esquerda no mobile: o rótulo "MAIOR" ocupa o canto superior direito. */}
        <GraficoTelaCheia ladoBotao="esquerda">
          {(telaCheia) => (
            <ReactECharts
              option={optionDoGrafico(!isMobile || telaCheia)}
              // Trocar entre o `option` do BE e o montado aqui não pode deixar chave velha.
              notMerge
              style={telaCheia ? { height: '100%', width: '100%' } : { height: 140 }}
            />
          )}
        </GraficoTelaCheia>
        <span className="dessem-subsistema-chart__menor">
          {`MENOR: ${formatDecimalBR(subsistema.menor)}`}
        </span>
      </Card>
    </div>
  );
}
